import styled, { css } from 'styled-components';
import { coreTokens } from '../../core';
import { useText } from '../../utils/hooks/use_text';
import { Icon } from '../icon';
import { TableAlign, TableColumn, TableProps, TableSort, TableSortDirection } from './table.component';
import { theme } from './table.theme';

function nextSortDirection(current: TableSort | undefined, columnKey: string): TableSortDirection {
	if (current?.columnKey === columnKey && current.direction === 'asc') {
		return 'desc';
	}
	return 'asc';
}

function justifyContentForAlign(align: TableAlign): string {
	if (align === 'center') return 'center';
	if (align === 'right') return 'flex-end';
	// Matches how a sortable header lays out today: label on the left, sort
	// indicator pinned to the trailing edge of the column.
	return 'space-between';
}

function columnLabel<TRow>(column: TableColumn<TRow>): string | undefined {
	if (column.label) return column.label;
	if (typeof column.header === 'string') return column.header;
	return undefined;
}

const sortIconStyles = css`
	& .cellula-table__sort-icon {
		flex: 0 0 auto;
		color: ${theme.getValue('headerSortIndicatorColor')};
		opacity: 0;
		transition: opacity 150ms;
	}

	&[data-sortable]:hover .cellula-table__sort-icon,
	&[aria-sort='ascending'] .cellula-table__sort-icon,
	&[aria-sort='descending'] .cellula-table__sort-icon {
		opacity: 1;
	}
	&[aria-sort='ascending'] .cellula-table__sort-icon {
		transform: rotate(180deg);
		color: ${theme.getValue('headerSortIndicatorActiveColor')};
	}
	&[aria-sort='descending'] .cellula-table__sort-icon {
		color: ${theme.getValue('headerSortIndicatorActiveColor')};
	}
`;

export const StyledHeaderCell = styled.th<{ $align: TableAlign }>`
	box-sizing: border-box;
	height: ${coreTokens.size.x6};
	vertical-align: middle;
	text-align: ${(props) => props.$align};
	cursor: default;
	white-space: nowrap;

	${sortIconStyles}

	&[aria-sort='ascending'],
	&[aria-sort='descending'] {
		color: ${theme.getValue('headerSortedTextColor')};
		position: relative;

		&::after {
			content: '';
			position: absolute;
			left: 0;
			bottom: -2px;
			width: 100%;
			height: 2px;
			background: ${theme.getValue('headerSortUnderlineColor')};
		}
	}
`;

const StyledHeaderCellStatic = styled.div`
	padding: ${coreTokens.spacing.x1} ${coreTokens.spacing.x1_5};
	font: ${theme.getValue('headerFont')};
`;

const StyledSortButton = styled.button<{ $align: TableAlign }>`
	all: unset;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: ${(props) => justifyContentForAlign(props.$align)};
	gap: ${coreTokens.spacing.x1_5};
	width: 100%;
	height: ${coreTokens.size.x6};
	padding: ${coreTokens.spacing.x1} ${coreTokens.spacing.x1_5};
	cursor: pointer;
	color: inherit;
	font: ${theme.getValue('headerFont')};

	&:focus-visible {
		outline: 2px solid ${theme.getValue('focusRingColor')};
		outline-offset: -2px;
	}
`;

export function TableHeaderCell<TRow>({
	column,
	sort,
	onSortChange,
}: {
	column: TableColumn<TRow>;
	sort: TableSort | undefined;
	onSortChange: TableProps<TRow>['onSortChange'];
}): JSX.Element {
	const staticTexts = useText();
	const align = column.align ?? 'left';

	if (!column.sortable) {
		return (
			<StyledHeaderCell $align={align} scope='col'>
				<StyledHeaderCellStatic>{column.header}</StyledHeaderCellStatic>
			</StyledHeaderCell>
		);
	}

	const isSorted = sort?.columnKey === column.key;
	let ariaSort: 'ascending' | 'descending' | 'none' = 'none';
	if (isSorted) {
		ariaSort = sort?.direction === 'asc' ? 'ascending' : 'descending';
	}

	const label = columnLabel(column);
	const sortButtonLabel = label
		? staticTexts('Component.Table.SortByColumn', { column: label })
		: staticTexts('Component.Table.SortByThisColumn');

	function handleClick(): void {
		onSortChange?.(column.key, nextSortDirection(sort, column.key));
	}

	return (
		<StyledHeaderCell $align={align} scope='col' data-sortable aria-sort={ariaSort}>
			<StyledSortButton type='button' $align={align} onClick={handleClick} aria-label={sortButtonLabel}>
				{column.header}
				<Icon className='cellula-table__sort-icon' type='arrowDown' size='small' />
			</StyledSortButton>
		</StyledHeaderCell>
	);
}
